import { FormRenderProps } from 'react-final-form';
import { Dispatch, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';

import styles from './PassportGroupRenderForm.module.scss';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IPassportGroupForm } from '../interfaces/IPassportGroupForm';
import { IGroupConditionForm } from '../interfaces/IGroupConditionForm';
import { PassportGroupConditionTree } from './PassportGroupConditionTree';
import { PassportGroupConditionForm } from './PassportGroupConditionForm';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { getConditionNameWithErrors } from '../common/getConditionNameWithErrors';
import { getEmptyConditionName } from '../common/getEmptyConditionName';
import { ConfirmModal } from '../../../UIPackage/MainModule/components/ConfirmModal';
import { PassportGroupTypeEnum } from '../../../APIPackage/PassportGroupsModule/enums/PassportGroupTypeEnum';

const CONDITION_ERROR = 'Требуется исправить ошибки';
const EMPTY_CONDITION_ERROR = 'В условии должен быть заполнен хотя бы один атрибут';

interface IPassportGroupRenderFormProps {
  onCancelCreate: () => void;
  groupType: PassportGroupTypeEnum;
}

export const PassportGroupRenderForm = ({
  form,
  values,
  handleSubmit,
  invalid,
  errors,
  onCancelCreate,
  groupType,
}: FormRenderProps<IPassportGroupForm, Partial<IPassportGroupForm>> & IPassportGroupRenderFormProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { push } = form.mutators;
  const [isShowCloseConfirmModal, setIsShowCloseConfirmModal] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState('includes[0]');

  const addInclude = useCallback(() => {
    const newInclude: IGroupConditionForm = {
      name: `Условие ${values.includes?.length ? values.includes?.length + 1 : 1}`,
    };

    push && push('includes', newInclude);
    setSelectedCondition(`includes[${values.includes?.length || 0}]`);
  }, [push, values, setSelectedCondition]);

  const addException = useCallback(() => {
    const newException: IGroupConditionForm = {
      name: `Исключение ${values.exceptions?.length ? values.exceptions.length + 1 : 1}`,
    };

    push && push('exceptions', newException);
    setSelectedCondition(`exceptions[${values.exceptions?.length || 0}]`);
  }, [push, values, setSelectedCondition]);

  const handleCloseButtonClick = useCallback(() => {
    setIsShowCloseConfirmModal(true);
  }, [setIsShowCloseConfirmModal]);

  const handleSaveButtonClick = useCallback(() => {
    if (!invalid) {
      const emptyConditionName = getEmptyConditionName(values);

      if (emptyConditionName) {
        dispatch(
          addNoticeAction({
            renderContent: EMPTY_CONDITION_ERROR,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.PASSPORT_GROUP,
          }),
        );
        setSelectedCondition(emptyConditionName);

        return;
      }
    }

    if (invalid) {
      const conditionNameWithErrors = getConditionNameWithErrors(errors);

      if (conditionNameWithErrors) {
        setSelectedCondition(conditionNameWithErrors);
      }

      dispatch(
        addNoticeAction({
          renderContent: CONDITION_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PASSPORT_GROUP,
        }),
      );
    }

    handleSubmit();
  }, [handleSubmit, values, dispatch, invalid, setSelectedCondition, errors]);

  return (
    <>
      <form className={styles.Form}>
        <div className={styles.Header}>
          <h1 className={styles.HeaderTitle}>Создание группы мероприятий</h1>
          <div className={styles.HeaderButtons}>
            <Button text="Добавить условие" type="button" onClick={addInclude} disabled={!!values.includes?.length} />
            <Button
              text="Добавить исключение"
              type="button"
              onClick={addException}
              disabled={!!values.exceptions?.length}
            />
            <Button
              text="Сохранить"
              color="primary"
              type="button"
              onClick={handleSaveButtonClick}
              disabled={!values.includes?.length && !values.exceptions?.length}
            />
            <Button text="Закрыть" type="button" onClick={handleCloseButtonClick} />
          </div>
        </div>
        <div className={styles.Content}>
          <PassportGroupConditionTree
            selectedCondition={selectedCondition}
            setSelectedCondition={setSelectedCondition}
          />
          <FieldArray name="includes">
            {({ fields }) =>
              fields.map(name => (
                <PassportGroupConditionForm
                  key={name}
                  name={name}
                  isVisible={name === selectedCondition}
                  groupType={groupType}
                />
              ))
            }
          </FieldArray>
          <FieldArray name="exceptions">
            {({ fields }) =>
              fields.map(name => (
                <PassportGroupConditionForm
                  key={name}
                  name={name}
                  isVisible={name === selectedCondition}
                  groupType={groupType}
                />
              ))
            }
          </FieldArray>
        </div>
      </form>
      <ConfirmModal
        title="Подтверждение"
        onApply={onCancelCreate}
        onClose={() => setIsShowCloseConfirmModal(false)}
        visible={isShowCloseConfirmModal}
        cancelButtonText="Отмена"
        text="Отменить создание группы и вернуться в реестр групп мероприятий, подлежащих отмене?"
      />
    </>
  );
};
